import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Layout from '../components/Layout';
import { useCan } from '../hooks/useMe';
import { useI18n } from '../i18n';
import api from '../services/api';
import {
  PARTIDAS, MODULOS, MODULE_CHOICES, ROLES, FASES, ST, NEXT, ESTANDAR, SUPPORT_LINE,
  normalize, summarize, num, text, fmtPlanDate,
} from '../implementation/catalog';
import { downloadPlanPdf } from '../implementation/downloadPlan';

const PLUM = '#531253';
const MAUVE = '#847782';
const HAIR = '#E4E0E2';

const STATUS_SX = {
  p: { color: MAUVE, backgroundColor: '#F5F1F4', borderColor: HAIR },
  c: { color: '#8A6508', backgroundColor: '#FBF1D6', borderColor: 'transparent' },
  h: { color: '#237A55', backgroundColor: '#E3F2EA', borderColor: 'transparent' },
};

const STRIPE = 'linear-gradient(90deg,#8E3A96 0 33.3%,#F5B400 33.3% 66.6%,#F2665E 66.6% 100%)';

function getPath(obj, path) {
  return path.split('.').reduce((a, k) => (a == null ? undefined : a[k]), obj);
}

// Copia inmutable con un valor cambiado en la ruta «a.b.c».
function setPath(obj, path, value) {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...obj, [head]: value };
  const child = obj[head] && typeof obj[head] === 'object' ? obj[head] : {};
  return { ...obj, [head]: setPath(child, rest.join('.'), value) };
}

/* ---------- piezas del formulario ---------- */

function Step({ n, title, hint, children }) {
  return (
    <Paper sx={{ p: 2.4, display: 'grid', gap: 1.8, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
          backgroundColor: '#F3E9F3', color: PLUM, fontWeight: 700, fontSize: 13.5,
        }}>{n}</Box>
        <Typography sx={{ fontSize: 16.5, fontWeight: 600 }}>{title}</Typography>
      </Box>
      {hint && <Typography sx={{ fontSize: 13, color: MAUVE, mt: -0.8 }}>{hint}</Typography>}
      {children}
    </Paper>
  );
}

const Grid = ({ children }) => (
  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
    {children}
  </Box>
);

// Tarjetas de opción única: el punto de partida y la configuración entre plantas.
function Options({ options, value, onChange, disabled, label }) {
  return (
    <Box role="radiogroup" aria-label={label} sx={{
      display: 'grid', gap: 1.2, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    }}>
      {options.map(([key, title, desc]) => {
        const on = value === key;
        return (
          <Box
            key={key} component="button" type="button" role="radio" aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(key)}
            sx={{
              textAlign: 'left', font: 'inherit', color: 'inherit', cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${on ? PLUM : HAIR}`, borderRadius: '6px', p: 1.4,
              backgroundColor: on ? '#F3E9F3' : '#fff', boxShadow: on ? `inset 0 0 0 1px ${PLUM}` : 'none',
              '&:hover:not(:disabled)': { borderColor: PLUM },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
            <Typography sx={{ fontSize: 12.5, color: MAUVE }}>{desc}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/* ---------- vista previa del documento ---------- */

const Tbd = ({ children = 'Por definir' }) => (
  <Box component="span" sx={{ color: MAUVE, fontStyle: 'italic' }}>{children}</Box>
);
const val = (v) => (text(v) ? text(v) : <Tbd />);

function DocSection({ title, children }) {
  return (
    <Box sx={{ display: 'grid', gap: 0.8 }}>
      <Typography sx={{ fontSize: 15.5, fontWeight: 600, pb: 0.5, borderBottom: `1px solid ${HAIR}` }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Kv({ rows }) {
  return (
    <Box component="dl" sx={{
      m: 0, display: 'grid', gap: '4px 16px', fontSize: 14,
      gridTemplateColumns: 'minmax(120px, 190px) minmax(0, 1fr)',
      '& dt': { color: MAUVE, fontSize: 13.5 }, '& dd': { m: 0 },
    }}>
      {rows.map(([k, v]) => [<dt key={`${k}-k`}>{k}</dt>, <dd key={`${k}-v`}>{v}</dd>])}
    </Box>
  );
}

function StatusTag({ s }) {
  return (
    <Box component="span" sx={{
      ...STATUS_SX[s], flexShrink: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em',
      textTransform: 'uppercase', borderRadius: '3px', px: 0.7, py: '1px', minWidth: 74,
      textAlign: 'center', alignSelf: 'flex-start', mt: '2px',
    }}>{ST[s]}</Box>
  );
}

function PlanPreview({ plan: p }) {
  const d = summarize(p);
  const resp = [text(p.resp.nombre), text(p.resp.puesto)].filter(Boolean).join(', ');
  const box = (title, items) => (
    <Box key={title} sx={{ backgroundColor: '#F5F1F4', borderRadius: '6px', p: 1.4 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 0.4 }}>{title}</Typography>
      {items.length
        ? <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: 13.5 }}>{items.map((x) => <li key={x}>{x}</li>)}</Box>
        : <Tbd>Ninguno</Tbd>}
    </Box>
  );

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ height: 6, background: STRIPE }} />
      <Box sx={{ p: { xs: 2.4, sm: 4 }, display: 'grid', gap: 2.6, fontSize: 14 }}>
        <Box>
          <Typography sx={{
            fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: PLUM,
          }}>MPTotal · Plan de implementación</Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15 }}>
            {text(p.cliente) || <Tbd>Nombre del cliente</Tbd>}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', color: MAUVE, fontSize: 13.5, mt: 0.8 }}>
            <span>Kickoff: {p.fecha ? fmtPlanDate(p.fecha) : 'por definir'}</span>
            <span>Customer Success: {text(p.csm) || 'por definir'}</span>
            <span>Avance: {d.progress.done} de {d.progress.total} tareas ({d.progress.pct}%)</span>
          </Box>
        </Box>

        <DocSection title="Equipo del proyecto">
          <Kv rows={[
            ['Responsable de implementación', resp || text(p.resp.correo)
              ? `${resp}${text(p.resp.correo) ? `${resp ? ' · ' : ''}${text(p.resp.correo)}` : ''}`
              : <Tbd />],
            ['Administrador de la cuenta', val(p.admin)],
            ['Customer Success MPTotal', val(p.csm)],
          ]} />
          <Typography sx={{ fontSize: 14 }}>
            El responsable coordina la información y los usuarios, asiste a la reunión semanal y valida cada fase antes de avanzar.
          </Typography>
        </DocSection>

        <DocSection title="Punto de partida">
          {d.partida ? (
            <>
              <Typography sx={{ fontSize: 14 }}><b>{d.partida.label}.</b> {d.partida.haremos}</Typography>
              {d.answers.map(([q, a]) => (
                <Box key={q}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{q}</Typography>
                  <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line' }}>{a}</Typography>
                </Box>
              ))}
            </>
          ) : <Tbd />}
        </DocSection>

        <DocSection title="Plantas y piloto">
          <Kv rows={[
            ['Plantas', d.plantas ? `${d.plantas}${text(p.plantas.ubic) ? ` · ${text(p.plantas.ubic)}` : ''}` : <Tbd />],
            ['Configuración', val(d.estandar)],
            ['Organización piloto', val(d.piloto)],
          ]} />
          <Typography sx={{ fontSize: 14 }}>
            Empezamos con {d.piloto || 'una organización'}, la dejamos funcionando y de ahí escalamos
            {d.plantas > 1 ? ' a las demás plantas' : ''}.
          </Typography>
        </DocSection>

        <DocSection title="Alcance">
          <Box sx={{ display: 'grid', gap: 1.2, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {box('Fase 1', d.fase1)}
            {box('Más adelante', d.despues)}
            {d.noAplica.length > 0 && box('No aplica', d.noAplica)}
          </Box>
          {d.porDecidir.length > 0 && <Tbd>Por decidir: {d.porDecidir.join(', ')}</Tbd>}
        </DocSection>

        <DocSection title="Usuarios y roles">
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'collapse', fontSize: 13.5,
              '& th, & td': { textAlign: 'left', p: '7px 8px', borderBottom: `1px solid ${HAIR}`, verticalAlign: 'top' },
              '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: MAUVE },
            }}>
              <thead><tr><th>Rol</th><th>Acceso</th><th>Qué hace</th><th style={{ textAlign: 'right' }}>Usuarios</th></tr></thead>
              <tbody>
                {ROLES.map((r) => (
                  <tr key={r.id}>
                    <td>{r.label}</td><td>{r.acceso}</td><td>{r.hace}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{num(p.usuarios[r.id]) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
          {d.totalUsers > 0 && <Typography sx={{ fontSize: 14 }}>Total: {d.totalUsers} usuarios.</Typography>}
        </DocSection>

        {text(p.integ) && (
          <DocSection title="Integraciones">
            <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line' }}>{text(p.integ)}</Typography>
          </DocSection>
        )}

        <DocSection title="Plan de trabajo">
          <Typography sx={{ fontSize: 14 }}>
            Ninguna fase avanza sin la aprobación del responsable de implementación.
          </Typography>
          {d.tasks.map((list, i) => (
            <Box key={FASES[i]}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{i + 1}. {FASES[i]}</Typography>
                <Typography sx={{ fontSize: 13, color: MAUVE, fontVariantNumeric: 'tabular-nums' }}>
                  {list.filter((t) => t.s === 'h').length}/{list.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gap: 0.4, mt: 0.5, mb: 1 }}>
                {list.map((t) => (
                  <Box key={t.id} sx={{ display: 'flex', gap: 1.2, fontSize: 13.5 }}>
                    <StatusTag s={t.s} /><span>{t.t}</span>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </DocSection>

        {text(p.notas) && (
          <DocSection title="Notas y riesgos">
            <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line' }}>{text(p.notas)}</Typography>
          </DocSection>
        )}

        <Typography sx={{ fontSize: 12.5, color: MAUVE, borderTop: `1px solid ${HAIR}`, pt: 1.4 }}>
          {SUPPORT_LINE}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ---------- pantalla ---------- */

export default function ImplementationPlan() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const source = params.get('source') || 'mpindustries';
  const navigate = useNavigate();
  const { t, fmtDateTime } = useI18n();
  const canEdit = useCan('edit_cs');

  const [account, setAccount] = useState(null);
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState('idle');   // idle | saving | saved | error
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // El último estado y el guardado pendiente viven en refs para poder
  // mandarlos al salir de la pantalla sin esperar al temporizador.
  const latest = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get(`/api/accounts/${tenantId}`, { params: { source } }),
      api.get(`/api/accounts/${tenantId}/implementation-plan`, { params: { source } }),
    ])
      .then(([a, p]) => {
        setAccount(a.data);
        if (p.data) {
          const data = normalize(p.data.data);
          setPlan(data);
          latest.current = data;
          setMeta(p.data);
        }
      })
      .catch((err) => setLoadError(err.response?.status === 403
        ? 'apiErrors.ACCOUNT_FORBIDDEN'
        : 'implementation.loadError'))
      .finally(() => setLoading(false));
  }, [tenantId, source]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    if (!latest.current) return Promise.resolve();
    setSaveState('saving');
    return api.put(
      `/api/accounts/${tenantId}/implementation-plan`,
      { data: latest.current },
      { params: { source } },
    )
      .then((res) => { setMeta(res.data); setSaveState('saved'); })
      .catch(() => setSaveState('error'));
  }, [tenantId, source]);

  // Si se sale con un guardado en espera, se manda antes de desmontar.
  useEffect(() => () => { if (timer.current) flush(); }, [flush]);

  function update(path, value) {
    if (!canEdit) return;
    setPlan((prev) => {
      const next = setPath(prev, path, value);
      latest.current = next;
      return next;
    });
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }

  function cycleTask(id) {
    update(`tareas.${id}`, NEXT[plan.tareas[id] || 'p']);
  }

  async function exportPdf() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadPlanPdf(plan);
    } catch (err) {
      console.error('downloadPlanPdf:', err);
      setExportError(t('implementation.exportError'));
    } finally {
      setExporting(false);
    }
  }

  const back = () => navigate(`/accounts/${tenantId}?source=${source}`);

  if (loading) return <Layout><Skeleton variant="rectangular" height={480} /></Layout>;
  if (loadError) return <Layout><Alert severity="error">{t(loadError)}</Alert></Layout>;

  if (account?.subscription_type !== 'ENTERPRISE' || !plan) {
    return (
      <Layout>
        <Button variant="text" onClick={back} sx={{ mb: 1, color: PLUM }}>{t('common.back')}</Button>
        <Alert severity="info">
          {account?.subscription_type !== 'ENTERPRISE'
            ? t('implementation.onlyEnterprise')
            : t('implementation.noPlan')}
        </Alert>
      </Layout>
    );
  }

  const field = (label, path, props = {}) => (
    <TextField
      label={label} size="small" fullWidth disabled={!canEdit}
      value={getPath(plan, path) ?? ''}
      onChange={(e) => update(path, e.target.value)}
      slotProps={{ inputLabel: { shrink: true } }}
      {...props}
    />
  );
  const area = (label, path, placeholder) => field(label, path, { multiline: true, minRows: 2, placeholder });

  const d = summarize(plan);
  const partida = PARTIDAS[plan.partida];
  const saveLabel = {
    idle: '',
    saving: t('implementation.saving'),
    saved: t('implementation.saved'),
    error: t('implementation.saveError'),
  }[saveState];

  return (
    <Layout>
      <Button variant="text" onClick={back} sx={{ mb: 1, color: PLUM }}>{t('common.back')}</Button>

      <Paper sx={{ p: 2.2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, mr: 'auto' }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: MAUVE,
          }}>{t('implementation.title')}</Typography>
          <Typography variant="h5">{account.organization_name}</Typography>
          {meta?.updated_at && (
            <Typography sx={{ fontSize: 12, color: MAUVE }}>
              {t('implementation.lastUpdate', {
                date: fmtDateTime(meta.updated_at),
                name: meta.updated_by_name || t('common.dash'),
              })}
            </Typography>
          )}
        </Box>
        <Typography aria-live="polite" sx={{
          fontSize: 12.5, color: saveState === 'error' ? '#AE5B61' : MAUVE, minWidth: 80,
        }}>{saveLabel}</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 12, color: MAUVE }}>{t('implementation.progress')}</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {d.progress.done}/{d.progress.total} · {d.progress.pct}%
          </Typography>
        </Box>
        <Button variant="contained" onClick={exportPdf} disabled={exporting}>
          {exporting ? t('implementation.exporting') : t('implementation.exportPdf')}
        </Button>
      </Paper>

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>{t('implementation.readOnly')}</Alert>}
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>{exportError}</Alert>
      )}

      <Box sx={{
        display: 'grid', gap: 2, alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.05fr) minmax(0, 1fr)' },
      }}>
        <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <Step n={1} title={t('implementation.steps.account')}>
            <Grid>
              {field(t('implementation.fields.client'), 'cliente')}
              {field(t('implementation.fields.kickoff'), 'fecha', { type: 'date' })}
              {field(t('implementation.fields.csm'), 'csm')}
            </Grid>
            <Box sx={{ backgroundColor: '#F3E9F3', borderRadius: '6px', p: 1.8, display: 'grid', gap: 1.4 }}>
              <Box>
                <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: PLUM }}>
                  {t('implementation.fields.owner')}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: MAUVE }}>{t('implementation.fields.ownerHint')}</Typography>
              </Box>
              <Grid>
                {field(t('implementation.fields.name'), 'resp.nombre', { sx: { backgroundColor: '#fff' } })}
                {field(t('implementation.fields.jobTitle'), 'resp.puesto', { sx: { backgroundColor: '#fff' } })}
                {field(t('implementation.fields.email'), 'resp.correo', { type: 'email', sx: { backgroundColor: '#fff' } })}
              </Grid>
            </Box>
            {field(t('implementation.fields.admin'), 'admin', {
              placeholder: t('implementation.fields.adminHint'),
            })}
          </Step>

          <Step n={2} title={t('implementation.steps.start')} hint={t('implementation.steps.startHint')}>
            <Options
              label={t('implementation.steps.start')}
              disabled={!canEdit}
              value={plan.partida}
              onChange={(v) => update('partida', v)}
              options={Object.entries(PARTIDAS).map(([k, o]) => [k, o.label, o.desc])}
            />
            {partida && (
              <Box sx={{ display: 'grid', gap: 1.4, borderLeft: `3px solid ${HAIR}`, pl: 1.8 }}>
                {partida.qs.map((q, i) => (
                  <Box key={q}>{area(q, `respPartida.${plan.partida}.${i}`)}</Box>
                ))}
              </Box>
            )}
          </Step>

          <Step n={3} title={t('implementation.steps.plants')} hint={t('implementation.steps.plantsHint')}>
            <Grid>
              {field(t('implementation.fields.plants'), 'plantas.total', {
                type: 'number', slotProps: { inputLabel: { shrink: true }, htmlInput: { min: 0 } },
              })}
              {field(t('implementation.fields.locations'), 'plantas.ubic')}
            </Grid>
            <Options
              label={t('implementation.steps.plants')}
              disabled={!canEdit}
              value={plan.plantas.estandar}
              onChange={(v) => update('plantas.estandar', v)}
              options={Object.entries(ESTANDAR).map(([k, o]) => [k, o.label, o.desc])}
            />
            {field(t('implementation.fields.pilot'), 'plantas.piloto', {
              placeholder: t('implementation.fields.pilotHint'),
            })}
          </Step>

          <Step n={4} title={t('implementation.steps.scope')} hint={t('implementation.steps.scopeHint')}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Activos y ajustes</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#237A55' }}>
                  {t('implementation.alwaysPhase1')}
                </Typography>
              </Box>
              {MODULOS.map((m) => (
                <Box key={m.id} sx={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
                  gap: 1, py: 1.1, borderTop: `1px solid ${HAIR}`,
                }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{m.label}</Typography>
                  <ToggleButtonGroup
                    exclusive size="small" disabled={!canEdit} aria-label={m.label}
                    value={plan.modulos[m.id] || null}
                    onChange={(e, v) => { if (v) update(`modulos.${m.id}`, v); }}
                    sx={{
                      '& .MuiToggleButton-root': { fontSize: 12, textTransform: 'none', px: 1.4, py: 0.4 },
                      '& .Mui-selected': { backgroundColor: `${PLUM} !important`, color: '#fff !important' },
                    }}
                  >
                    {MODULE_CHOICES.map(([v, l]) => <ToggleButton key={v} value={v}>{l}</ToggleButton>)}
                  </ToggleButtonGroup>
                </Box>
              ))}
            </Box>
          </Step>

          <Step n={5} title={t('implementation.steps.users')}>
            <Box>
              {ROLES.map((r, i) => (
                <Box key={r.id} sx={{
                  display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 90px', gap: 1.5, alignItems: 'center',
                  py: 1.1, borderTop: i ? `1px solid ${HAIR}` : 'none',
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: MAUVE }}>{r.acceso} · {r.hace}</Typography>
                  </Box>
                  <TextField
                    size="small" type="number" placeholder="#" disabled={!canEdit}
                    value={plan.usuarios[r.id]}
                    onChange={(e) => update(`usuarios.${r.id}`, e.target.value)}
                    slotProps={{ htmlInput: { min: 0, 'aria-label': r.label } }}
                  />
                </Box>
              ))}
            </Box>
          </Step>

          <Step n={6} title={t('implementation.steps.notes')}>
            {area(t('implementation.fields.integrations'), 'integ', t('implementation.fields.integrationsHint'))}
            {area(t('implementation.fields.notes'), 'notas', t('implementation.fields.notesHint'))}
          </Step>

          <Step n={7} title={t('implementation.steps.tracking')} hint={t('implementation.steps.trackingHint')}>
            {d.tasks.map((list, i) => {
              const done = list.filter((x) => x.s === 'h').length;
              return (
                <Box key={FASES[i]} sx={{ pt: i ? 1.4 : 0, borderTop: i ? `1px solid ${HAIR}` : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.6 }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{i + 1}. {FASES[i]}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12.5, color: MAUVE, fontVariantNumeric: 'tabular-nums' }}>
                        {done}/{list.length}
                      </Typography>
                      <Box sx={{ width: 90, height: 6, borderRadius: 3, backgroundColor: '#EDE6E3', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', backgroundColor: '#237A55',
                          width: `${list.length ? (done * 100) / list.length : 0}%`,
                        }} />
                      </Box>
                    </Box>
                  </Box>
                  {list.map((task) => (
                    <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.4 }}>
                      <Box
                        component="button" type="button" disabled={!canEdit}
                        onClick={() => cycleTask(task.id)}
                        aria-label={`${task.t}: ${ST[task.s]}`}
                        sx={{
                          ...STATUS_SX[task.s], flexShrink: 0, minWidth: 96, border: '1px solid',
                          borderRadius: 999, px: 1.2, py: 0.3, font: 'inherit', fontSize: 12, fontWeight: 700,
                          cursor: canEdit ? 'pointer' : 'default',
                        }}
                      >{ST[task.s]}</Box>
                      <Typography sx={{ fontSize: 14, color: task.s === 'h' ? MAUVE : 'inherit' }}>{task.t}</Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Step>
        </Box>

        <Box sx={{
          position: { lg: 'sticky' }, top: { lg: 16 }, minWidth: 0,
          maxHeight: { lg: 'calc(100vh - 32px)' }, overflow: { lg: 'auto' },
        }}>
          <PlanPreview plan={plan} />
        </Box>
      </Box>
    </Layout>
  );
}
